import * as React from 'react';
import { useState, useEffect } from 'react';
import MuiAvatar from '@mui/material/Avatar';
import MuiListItemAvatar from '@mui/material/ListItemAvatar';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Select, { selectClasses } from '@mui/material/Select';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { getOrganizations } from '../services/api';
import { useGlobalContext } from '../contexts/GlobalContext';

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.secondary,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
}));

const ListItemAvatar = styled(MuiListItemAvatar)({
  minWidth: 0,
  marginRight: 12,
});

export default function OrganizationSelector() {
  const {
    selectedOrganization,
    setSelectedOrganization,
    isLoading: contextLoading,
  } = useGlobalContext();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrganizations();
  }, []);

  // Additional effect to handle default selection after context loads
  useEffect(() => {
    // Only set default organization after:
    // 1. Context has finished loading from storage
    // 2. Organizations have been loaded
    // 3. No organization is currently selected
    // 4. There are organizations available
    if (
      !contextLoading &&
      !loading &&
      organizations.length > 0 &&
      !selectedOrganization
    ) {
      setSelectedOrganization(organizations[0]);
    }
  }, [
    contextLoading,
    loading,
    organizations,
    selectedOrganization,
    setSelectedOrganization,
  ]);

  // Additional effect to validate that selectedOrganization exists in current organizations
  useEffect(() => {
    if (
      !contextLoading &&
      !loading &&
      selectedOrganization &&
      organizations.length > 0
    ) {
      const orgExists = organizations.find(
        org => org.id === selectedOrganization.id
      );
      if (!orgExists) {
        setSelectedOrganization(null);
      }
    }
  }, [
    contextLoading,
    loading,
    selectedOrganization,
    organizations,
    setSelectedOrganization,
  ]);

  const loadOrganizations = async () => {
    try {
      const orgsData = await getOrganizations();
      const orgsList = Array.isArray(orgsData?.results) ? orgsData.results : [];
      setOrganizations(orgsList);

      // Remove the immediate default selection logic - let the useEffect handle it
      // This prevents race conditions with the GlobalContext loading from storage
    } catch (error) {
      console.error('Error loading organizations:', error);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = event => {
    const value = event.target.value;
    const organization = organizations.find(org => org.id === value);
    setSelectedOrganization(organization || null);
  };

  // Don't render the select until we have organizations loaded
  // This prevents MUI from complaining about out-of-range values
  if (loading || contextLoading || organizations.length === 0) {
    return (
      <Select
        disabled
        displayEmpty
        fullWidth
        sx={{
          minHeight: 56,
          width: 240,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pl: 2,
            pr: 4,
          },
        }}
      >
        <MenuItem value="">
          <ListItemText
            primary={
              loading || contextLoading
                ? 'Loading organizations...'
                : 'No organizations'
            }
          />
        </MenuItem>
      </Select>
    );
  }

  return (
    <Select
      key={`org-selector-${selectedOrganization?.id || 'none'}`}
      labelId="organization-select"
      id="organization-simple-select"
      value={selectedOrganization?.id ?? ''}
      onChange={handleChange}
      displayEmpty
      inputProps={{ 'aria-label': 'Select organization' }}
      fullWidth
      renderValue={value => {
        if (!value || !selectedOrganization) {
          return (
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{ width: '100%', overflow: 'hidden' }}
            >
              <Avatar>
                <BusinessRoundedIcon sx={{ fontSize: '1rem' }} />
              </Avatar>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  noWrap
                  sx={{ lineHeight: 1.2, mb: 0, padding: 0, margin: 0 }}
                >
                  Select organization
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ lineHeight: 1.1, mt: 0, padding: 0, margin: 0 }}
                >
                  Choose your workspace
                </Typography>
              </Box>
            </Box>
          );
        }

        return (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ width: '100%', overflow: 'hidden' }}
          >
            <Avatar>
              <BusinessRoundedIcon sx={{ fontSize: '1rem' }} />
            </Avatar>
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                noWrap
                sx={{ lineHeight: 1.2, mb: 0, padding: 0, margin: 0 }}
              >
                {selectedOrganization.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ lineHeight: 1.1, mt: 0, padding: 0, margin: 0 }}
              >
                {selectedOrganization.description || 'Organization'}
              </Typography>
            </Box>
          </Box>
        );
      }}
      sx={{
        minHeight: 56,
        width: 240, // Increased width to accommodate longer text
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pl: 2,
          pr: 4, // Add right padding to prevent text from being cut off
        },
        [`& .${selectClasses.select}`]: {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pl: 2,
          pr: 4,
        },
      }}
    >
      {organizations.length === 0 && !loading
        ? [
            <MenuItem key="no-orgs" value="">
              <ListItemText
                primary="No organizations"
                secondary="Contact admin to join an organization"
              />
            </MenuItem>,
          ]
        : [
            <ListSubheader key="header" sx={{ pt: 0 }}>
              Organizations
            </ListSubheader>,
            ...organizations.map(org => (
              <MenuItem key={org.id} value={org.id}>
                <ListItemAvatar>
                  <Avatar alt={org.name}>
                    <BusinessRoundedIcon sx={{ fontSize: '1rem' }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={org.name}
                  secondary={org.description || 'No description'}
                />
              </MenuItem>
            )),
          ]}
    </Select>
  );
}
